import {
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn as amplifySignIn,
  signOut,
  signUp as amplifySignUp,
  updatePassword,
} from "aws-amplify/auth";

const toLegacyToken = (token) => {
  if (!token) return null;
  const jwtToken = token.toString();
  return {
    ...token,
    jwtToken,
    payload: token.payload || {},
    getJwtToken: () => jwtToken,
  };
};

const normalizeChallengeName = (nextStep) => {
  switch (nextStep?.signInStep) {
    case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
      return "NEW_PASSWORD_REQUIRED";
    case "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE":
      return "CUSTOM_CHALLENGE";
    case "CONFIRM_SIGN_UP":
      return "CONFIRM_SIGN_UP";
    case "DONE":
      return null;
    default:
      return nextStep?.signInStep || null;
  }
};

const currentSession = async () => {
  const session = await fetchAuthSession();
  if (!session.tokens?.idToken || !session.tokens?.accessToken) {
    throw new Error("No current authenticated user");
  }

  const idToken = toLegacyToken(session.tokens.idToken);
  const accessToken = toLegacyToken(session.tokens.accessToken);

  return {
    ...session,
    idToken,
    accessToken,
    getIdToken: () => idToken,
    getAccessToken: () => accessToken,
  };
};

const currentAuthenticatedUser = async () => {
  const [user, attributes, signInUserSession] = await Promise.all([
    getCurrentUser(),
    fetchUserAttributes(),
    currentSession(),
  ]);

  return {
    ...user,
    attributes,
    signInUserSession,
  };
};

const currentUserInfo = async () => {
  const user = await getCurrentUser();
  const attributes = await fetchUserAttributes();

  return {
    ...user,
    attributes,
  };
};

const buildPendingSignInResult = (result) => ({
  ...result,
  challengeName: normalizeChallengeName(result.nextStep),
  signInUserSession: null,
});

const signIn = async (username, password) => {
  const input = { username };
  if (password !== undefined) {
    input.password = password;
  } else {
    input.options = { authFlowType: "CUSTOM_WITHOUT_SRP" };
  }

  const result = await amplifySignIn(input);
  return result.isSignedIn
    ? currentAuthenticatedUser()
    : buildPendingSignInResult(result);
};

const sendCustomChallengeAnswer = async (_user, challengeResponse) => {
  const result = await confirmSignIn({ challengeResponse });
  return result.isSignedIn
    ? currentAuthenticatedUser()
    : buildPendingSignInResult(result);
};

const completeNewPassword = async (_user, newPassword) => {
  const result = await confirmSignIn({ challengeResponse: newPassword });
  return result.isSignedIn
    ? currentAuthenticatedUser()
    : buildPendingSignInResult(result);
};

const signUp = ({ username, password, attributes, options }) =>
  amplifySignUp({
    username,
    password,
    options: {
      ...options,
      userAttributes: attributes || options?.userAttributes,
    },
  });

export const Auth = {
  currentAuthenticatedUser,
  currentSession,
  currentUserInfo,
  signIn,
  signOut,
  sendCustomChallengeAnswer,
  completeNewPassword,
  changePassword: async (_user, oldPassword, newPassword) => {
    await updatePassword({ oldPassword, newPassword });
    return "SUCCESS";
  },
  forgotPassword: (username) => resetPassword({ username }),
  forgotPasswordSubmit: (username, confirmationCode, newPassword) =>
    confirmResetPassword({ username, confirmationCode, newPassword }),
  signUp,
  confirmSignUp: (username, confirmationCode) =>
    confirmSignUp({ username, confirmationCode }),
  resendSignUp: (username) => resendSignUpCode({ username }),
};
